import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import type { HistoryItem } from '@platform/contracts';
import { useT, formatAmount, formatCurrencyUnit } from '@core/i18n';
import {
  betHistoryModalStyles as baseStyles,
  BET_HISTORY_GRID_COLUMNS,
  BET_HISTORY_GRID_COLUMNS_WITH_BALANCES,
  BET_HISTORY_SUMMARY_GRID_COLUMNS,
  BET_HISTORY_DETAIL_GRID_COLUMNS,
  BET_HISTORY_TABLE_MIN_WIDTH_PX,
} from '@core/UI/styles/BetHistoryModal.styles';
import {
  BetHistoryService,
  type BetHistoryGroup,
  type BetHistoryRound,
} from '@core/Web/services/BetHistoryService';
import { getGameSessionId } from '@core/utils/gameSession';
import { ProvablyFairService } from '@core/Web/services/provablyfairservice';
import { resolveGameStoreHook } from '@core/gameengine/state/activeGameStore';
import {
  resolveBetHistoryMultiplierColor,
  type BetHistoryMultiplierColors,
} from '@core/gameengine/controller/BaseGameController';
import { getGameFromUrl } from '@core/utils/urlParser';
import { isSkyxNewRoute } from '@core/utils/gameRouteSlugs';
import { SkyxNewBetHistoryLogsButton } from '@core/UI/skyx/SkyxNewBetHistoryLogsButton';
import { SkyxNewModalCloseButton } from '@core/UI/skyx/SkyxNewModalCloseButton';
import { SKYX_MODAL_CLOSE } from '@core/UI/skyx/SkyxNewModalClose.styles';
import { SkyxNewModalPanelShell } from '@core/UI/skyx/SkyxNewModalPanelShell';
import {
  buildGamePublicAssetUrl,
  resolveAssetFolderSlug,
} from '@core/utils/assetFolderSlug';
import { getAssetManifest } from '@core/UI/utils/getAssetHelper';
import { buildGameLogsUrl } from '@core/utils/gameLogsUrl';
import { getLobbyUrlFromGameQuery } from '@config/runtime.config';
import { useAuth } from '@core/gameengine/state/auth';
import { WalletBalance } from '@core/UI/wallet/WalletBalance';

const TABLE_NO = 1;

function collectBetHistoryRoundKeys(round: BetHistoryRound): string[] {
  const keys = new Set<string>();
  for (const value of [round.roundId, round.roundNo]) {
    if (value == null) continue;
    const key = String(value);
    if (!key || key === 'undefined' || key === 'null') continue;
    keys.add(key);
  }
  return [...keys];
}

const ShieldIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
      stroke="#28a909"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="m9 12 2 2 4-4"
      stroke="#28a909"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WinCheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 10 10" fill="none" aria-hidden>
    <path
      d="M2 5.2 4.1 7.3 8 3.4"
      stroke="#ffffff"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChatIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z"
      stroke="#28a909"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ExpandToggleIcon = ({ expanded }: { expanded: boolean }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path
      d="M2 6h8"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    {!expanded && (
      <path
        d="M6 2v8"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    )}
  </svg>
);

function formatBetAmount(value: number): string {
  return formatAmount(value);
}

function formatBalanceAmount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  return formatAmount(value);
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function formatDateParts(timestamp: number): { time: string; date: string } {
  const d = new Date(timestamp);
  const date = `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
  const time = `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
  return { time, date };
}

function mergeBetHistoryStyles(
  overrides?: Record<string, React.CSSProperties>
): Record<string, React.CSSProperties> {
  if (!overrides) return baseStyles;

  const merged = { ...baseStyles };
  for (const key of Object.keys(overrides)) {
    const base = merged[key];
    const patch = overrides[key];
    if (base && patch) {
      merged[key] = { ...base, ...patch };
    } else if (patch) {
      merged[key] = patch;
    }
  }
  return merged;
}

export interface BetHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BetHistoryModal: React.FC<BetHistoryModalProps> = ({
  isOpen,
  onClose,
}) => {
  const gameSlug = getGameFromUrl() ?? 'crash';
  const isSkyxNew = isSkyxNewRoute(gameSlug);
  const t = useT(isSkyxNew ? 'skyx_new' : undefined);
  const [visible, setVisible] = useState(false);
  const [groups, setGroups] = useState<BetHistoryGroup[]>([]);
  const [expandedRoundIds, setExpandedRoundIds] = useState<Set<string>>(
    () => new Set()
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const controller = (window as any).playgrid?.getCurrentGameController?.();
  const platformUIConfig = controller?.getPlatformUIConfig?.() ?? {};
  const betHistoryStyleOverrides = platformUIConfig.betHistoryStyleOverrides;
  const betHistoryShowBalances =
    platformUIConfig.betHistoryShowBalances === true;
  const betHistoryMultiplierColors =
    platformUIConfig.betHistoryMultiplierColors as
      | BetHistoryMultiplierColors
      | undefined;
  const betHistoryModalCss = platformUIConfig.betHistoryModalCss as
    | string
    | undefined;
  const useGameStore = resolveGameStoreHook(controller);
  const deviceType = useGameStore(
    (state: { deviceType?: 'desktop' | 'tablet' | 'mobile' }) =>
      state.deviceType ?? 'desktop'
  );
  const useMobileCentered = isSkyxNew && deviceType !== 'desktop';
  const horizontalScrollRef = useRef<HTMLDivElement>(null);
  const [horizontalScrollRatio, setHorizontalScrollRatio] = useState(0);
  const [horizontalScrollMax, setHorizontalScrollMax] = useState(0);
  const summaryGridColumns = BET_HISTORY_SUMMARY_GRID_COLUMNS;
  const detailGridColumns = BET_HISTORY_DETAIL_GRID_COLUMNS;
  const gridColumns = betHistoryShowBalances
    ? BET_HISTORY_GRID_COLUMNS_WITH_BALANCES
    : BET_HISTORY_GRID_COLUMNS;
  const tableMinWidth = betHistoryShowBalances
    ? BET_HISTORY_TABLE_MIN_WIDTH_PX
    : undefined;
  const gridStyle = useMemo(
    () => ({ gridTemplateColumns: gridColumns }),
    [gridColumns]
  );
  const summaryGridStyle = useMemo(
    () => ({ gridTemplateColumns: summaryGridColumns }),
    [summaryGridColumns]
  );
  const detailGridStyle = useMemo(
    () => ({ gridTemplateColumns: detailGridColumns }),
    [detailGridColumns]
  );
  const s = useMemo(
    () => mergeBetHistoryStyles(betHistoryStyleOverrides),
    [betHistoryStyleOverrides]
  );
  const rounds = useMemo(() => groups.flatMap(group => group.bets), [groups]);
  const authUser = useAuth(state => state.user);
  const gameLogsUrl = useMemo(
    () => buildGameLogsUrl({ lobbyUrl: getLobbyUrlFromGameQuery() }),
    [authUser]
  );
  const currency = useGameStore(
    (state: { currency?: string }) => state.currency
  );
  const openProvablyFairModal = useGameStore(
    (state: { openProvablyFairModal?: (data: HistoryItem) => void }) =>
      state.openProvablyFairModal
  );
  const closeProvablyFairModal = useGameStore(
    (state: { closeProvablyFairModal?: () => void }) =>
      state.closeProvablyFairModal
  );
  const provablyFairIsOpen = useGameStore(
    (state: { provablyFairModal?: { isOpen?: boolean } }) =>
      state.provablyFairModal?.isOpen
  );
  const nestedPfOpenedRef = useRef(false);
  useEffect(() => {
    if (!provablyFairIsOpen) nestedPfOpenedRef.current = false;
  }, [provablyFairIsOpen]);
  const handleClose = useCallback(() => {
    if (nestedPfOpenedRef.current) {
      nestedPfOpenedRef.current = false;
      closeProvablyFairModal?.();
    }
    onClose();
  }, [closeProvablyFairModal, onClose]);
  const assetManager = useGameStore(
    (state: { assetManager?: { baseUrl?: string } }) => state.assetManager
  );
  const liveRoundId = useGameStore(
    (state: { roundId?: string }) => state.roundId
  );
  const liveRoundNo = useGameStore(
    (state: { roundNo?: number }) => state.roundNo
  );
  const livePhase = useGameStore((state: { phase?: string }) => state.phase);
  const crashedAtX = useGameStore(
    (state: { crashedAtX?: number | null }) => state.crashedAtX
  );
  const crashHistory = useGameStore(
    (state: { history?: HistoryItem[] }) => state.history ?? []
  );
  const crashedRoundIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of crashHistory) {
      if (item?.roundId != null) {
        ids.add(String(item.roundId));
      }
    }
    return ids;
  }, [crashHistory]);

  const crashMultiplierByRoundId = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of crashHistory) {
      if (item?.roundId != null && item.bust != null && item.bust > 0) {
        map.set(String(item.roundId), item.bust);
      }
    }
    return map;
  }, [crashHistory]);

  const isLiveRoundStillInFlight =
    livePhase === 'WAITING' ||
    livePhase === 'BET_LOCK' ||
    livePhase === 'TAKEOFF' ||
    livePhase === 'FLYING';

  const isLiveRoundSettled =
    livePhase === 'CRASHED' ||
    livePhase === 'FLEW_AWAY' ||
    livePhase === 'REVEAL';

  const isCurrentLiveRound = useCallback(
    (round: BetHistoryRound): boolean => {
      const ids = new Set(collectBetHistoryRoundKeys(round));
      if (ids.size === 0) return false;
      if (liveRoundNo != null && ids.has(String(liveRoundNo))) return true;
      if (liveRoundId && ids.has(String(liveRoundId))) return true;
      return false;
    },
    [liveRoundId, liveRoundNo]
  );

  const roundAppearsInCrashHistory = useCallback(
    (round: BetHistoryRound): boolean =>
      collectBetHistoryRoundKeys(round).some(key => crashedRoundIds.has(key)),
    [crashedRoundIds]
  );

  const isRoundInProgress = useCallback(
    (round: BetHistoryRound, rowIndex: number): boolean => {
      if (!isSkyxNew) return false;

      if (round.cashOutAmount != null && round.cashOutAmount > 0) {
        return false;
      }
      if (round.multiplier > 0) {
        return false;
      }
      if (round.isBusted) {
        return false;
      }

      if (roundAppearsInCrashHistory(round)) {
        return false;
      }

      if (isCurrentLiveRound(round) && isLiveRoundStillInFlight) {
        return true;
      }

      // Fallback when round ids differ between API and socket payloads.
      return (
        liveRoundNo == null &&
        liveRoundId == null &&
        rowIndex === 0 &&
        isLiveRoundStillInFlight
      );
    },
    [
      isCurrentLiveRound,
      isLiveRoundStillInFlight,
      isSkyxNew,
      liveRoundId,
      liveRoundNo,
      roundAppearsInCrashHistory,
    ]
  );

  const resolveCrashMultiplier = useCallback(
    (round: BetHistoryRound, rowIndex?: number): number => {
      if (round.crashMultiplier > 0) {
        return round.crashMultiplier;
      }

      for (const key of collectBetHistoryRoundKeys(round)) {
        const fromCrashHistory = crashMultiplierByRoundId.get(key);
        if (fromCrashHistory != null && fromCrashHistory > 0) {
          return fromCrashHistory;
        }
      }

      const isLiveRound = isCurrentLiveRound(round);

      if (
        (isLiveRound || rowIndex === 0) &&
        crashedAtX != null &&
        crashedAtX > 0 &&
        isLiveRoundSettled
      ) {
        return crashedAtX;
      }

      return 0;
    },
    [
      crashMultiplierByRoundId,
      crashedAtX,
      isCurrentLiveRound,
      isLiveRoundSettled,
    ]
  );

  const canOpenProvablyFairForRound = useCallback(
    (round: BetHistoryRound): boolean => {
      if (!isSkyxNew) return true;

      if (collectBetHistoryRoundKeys(round).length === 0) {
        return false;
      }

      // PF shows the final crash multiplier — keep the shield off until crash.
      if (resolveCrashMultiplier(round) > 0) return true;
      if (roundAppearsInCrashHistory(round)) return true;
      if (isCurrentLiveRound(round) && isLiveRoundSettled) return true;

      return false;
    },
    [
      isCurrentLiveRound,
      isLiveRoundSettled,
      isSkyxNew,
      resolveCrashMultiplier,
      roundAppearsInCrashHistory,
    ]
  );
  const getMyBetsMultiplier = useCallback((round: BetHistoryRound): number => {
    if (round.multiplier > 0) return round.multiplier;
    if (round.crashMultiplier > 0) return round.crashMultiplier;
    return 0;
  }, []);

  const resolveDisplayMultiplier = useCallback(
    (round: BetHistoryRound, rowIndex: number): number => {
      if (round.cashOutAmount != null && round.cashOutAmount > 0) {
        return round.multiplier;
      }
      if (round.multiplier > 0) {
        return round.multiplier;
      }

      for (const key of collectBetHistoryRoundKeys(round)) {
        const fromCrashHistory = crashMultiplierByRoundId.get(key);
        if (fromCrashHistory != null && fromCrashHistory > 0) {
          return fromCrashHistory;
        }
      }

      if (
        isCurrentLiveRound(round) &&
        crashedAtX != null &&
        crashedAtX > 0 &&
        isLiveRoundSettled
      ) {
        return crashedAtX;
      }

      if (
        rowIndex === 0 &&
        crashedAtX != null &&
        crashedAtX > 0 &&
        isLiveRoundSettled
      ) {
        return crashedAtX;
      }

      return round.multiplier;
    },
    [
      crashMultiplierByRoundId,
      crashedAtX,
      isCurrentLiveRound,
      isLiveRoundSettled,
    ]
  );

  const assetFolder = resolveAssetFolderSlug(gameSlug);
  const manifest = getAssetManifest(gameSlug);
  const shieldIconUrl = useMemo(() => {
    const path = manifest?.images?.sheildIcon ?? manifest?.images?.shieldIcon;
    if (path) {
      return (
        controller?.getAssetUrl?.(path) ??
        buildGamePublicAssetUrl(assetFolder, path, assetManager?.baseUrl)
      );
    }
    return null;
  }, [assetFolder, assetManager?.baseUrl, controller, manifest]);

  const chatIconUrl = useMemo(() => {
    const path = manifest?.images?.chatIcon;
    if (path) {
      return (
        controller?.getAssetUrl?.(path) ??
        buildGamePublicAssetUrl(assetFolder, path, assetManager?.baseUrl)
      );
    }
    return null;
  }, [assetFolder, assetManager?.baseUrl, controller, manifest]);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const gameSessionId = getGameSessionId();
      const data = await BetHistoryService.getInstance().getSessionRounds(
        gameSessionId ?? undefined
      );
      setGroups(data);
    } catch (err) {
      console.error('[BetHistoryModal] Failed to load bet history:', err);
      setGroups([]);
      setError(
        err instanceof Error
          ? err.message
          : t('ui.popuppanel.bet_history.load_error')
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setVisible(true), 10);
      void loadHistory();
      return () => clearTimeout(timer);
    }
    setVisible(false);
    setGroups([]);
    setExpandedRoundIds(new Set());
    setError(null);
  }, [isOpen, loadHistory]);

  const syncHorizontalScrollMetrics = useCallback(() => {
    const el = horizontalScrollRef.current;
    if (!el) {
      setHorizontalScrollMax(0);
      setHorizontalScrollRatio(0);
      return;
    }
    const max = Math.max(0, el.scrollWidth - el.clientWidth);
    setHorizontalScrollMax(max);
    setHorizontalScrollRatio(max > 0 ? el.scrollLeft / max : 0);
  }, []);

  useEffect(() => {
    if (!isOpen || !visible || isSkyxNew) return;

    const el = horizontalScrollRef.current;
    syncHorizontalScrollMetrics();

    const raf = requestAnimationFrame(syncHorizontalScrollMetrics);
    const onScroll = () => syncHorizontalScrollMetrics();
    el?.addEventListener('scroll', onScroll, { passive: true });

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => syncHorizontalScrollMetrics())
        : null;
    if (el && resizeObserver) {
      resizeObserver.observe(el);
      const content = el.firstElementChild;
      if (content instanceof HTMLElement) {
        resizeObserver.observe(content);
      }
    }

    window.addEventListener('resize', syncHorizontalScrollMetrics);

    return () => {
      cancelAnimationFrame(raf);
      el?.removeEventListener('scroll', onScroll);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', syncHorizontalScrollMetrics);
    };
  }, [
    isOpen,
    visible,
    isSkyxNew,
    rounds.length,
    groups.length,
    loading,
    betHistoryShowBalances,
    syncHorizontalScrollMetrics,
  ]);

  const handleHorizontalSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const el = horizontalScrollRef.current;
      if (!el) return;
      const ratio = Number(e.target.value);
      const max = Math.max(0, el.scrollWidth - el.clientWidth);
      el.scrollTo({ left: ratio * max, behavior: 'smooth' });
      setHorizontalScrollRatio(ratio);
    },
    []
  );

  const showHorizontalSlider = !isSkyxNew && horizontalScrollMax > 1;

  const toggleGroupExpanded = useCallback((roundId: string) => {
    setExpandedRoundIds(prev => {
      const next = new Set(prev);
      if (next.has(roundId)) {
        next.delete(roundId);
      } else {
        next.add(roundId);
      }
      return next;
    });
  }, []);

  const handleShieldClick = async (
    e: React.MouseEvent,
    round: BetHistoryRound
  ) => {
    e.stopPropagation();
    if (!openProvablyFairModal) return;
    if (!canOpenProvablyFairForRound(round)) return;

    const crashX = resolveCrashMultiplier(round);
    const roundNo = Number(round.roundNo ?? round.roundId);
    if (!Number.isNaN(roundNo) && roundNo > 0) {
      try {
        // skyx_new: open the audit/verify modal; it loads seeds itself.
        // Only allow once the round has crashed (final multiplier available).
        if (isSkyxNew) {
          openProvablyFairModal({
            roundId: roundNo,
            bust: crashX,
            serverSeed: '',
            serverSeedHash: '',
            timeStamp: round.timestamp,
          } as HistoryItem);
          nestedPfOpenedRef.current = true;
          return;
        }

        const roundData =
          await ProvablyFairService.getInstance().getRoundHistory(
            round.tableNo ?? TABLE_NO,
            roundNo
          );
        if (roundData?.serverSeed) {
          openProvablyFairModal({
            roundId: String(roundData.roundNo ?? round.roundId),
            bust: roundData.crashX ?? crashX,
            serverSeed: roundData.serverSeed,
            clientSeed: roundData.clientSeed ?? 'N/A',
            serverSeedHash: roundData.serverSeedHash ?? 'N/A',
            timeStamp: round.timestamp,
          } as HistoryItem);
          nestedPfOpenedRef.current = true;
          return;
        }
      } catch (fetchError) {
        console.error(
          '[BetHistoryModal] Provably fair fetch failed:',
          fetchError
        );
      }
    }

    if (round.serverSeed) {
      openProvablyFairModal({
        roundId: round.roundId,
        bust: crashX,
        serverSeed: round.serverSeed,
        clientSeed: round.clientSeed ?? 'N/A',
        serverSeedHash: round.serverSeedHash ?? 'N/A',
        timeStamp: round.timestamp,
      } as HistoryItem);
      nestedPfOpenedRef.current = true;
    }
  };

  if (!isOpen) return null;

  const displayCurrency = formatCurrencyUnit(currency ?? 'USD');
  const betColumnLabel = t('ui.popuppanel.bet_history.bet_column', {
    currency: displayCurrency,
  });
  const cashOutColumnLabel = t('ui.popuppanel.bet_history.cash_out_column', {
    currency: displayCurrency,
  });

  const startBalanceColumnLabel = t(
    'ui.popuppanel.bet_history.start_balance_column',
    { currency: displayCurrency }
  );
  const endBalanceColumnLabel = t(
    'ui.popuppanel.bet_history.end_balance_column',
    { currency: displayCurrency }
  );
  const totalBetColumnLabel = t('ui.popuppanel.bet_history.total_bet_column', {
    currency: displayCurrency,
  });
  const totalWinColumnLabel = t('ui.popuppanel.bet_history.total_win_column', {
    currency: displayCurrency,
  });

  const renderShieldButton = (round: BetHistoryRound) => {
    const pfEnabled = canOpenProvablyFairForRound(round);
    return (
      <button
        type="button"
        style={{
          ...s.actionButton,
          ...(pfEnabled
            ? {}
            : {
                opacity: 0.35,
                cursor: 'not-allowed',
              }),
        }}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
          if (!pfEnabled) return;
          void handleShieldClick(e, round);
        }}
        onMouseDown={e => e.stopPropagation()}
        onPointerDown={e => e.stopPropagation()}
        disabled={!pfEnabled}
        aria-disabled={!pfEnabled}
        aria-label={t('ui.popuppanel.provably_fair.provably_fair')}
        title={
          pfEnabled
            ? undefined
            : t('ui.popuppanel.provably_fair.pending_reveal')
        }
      >
        {shieldIconUrl ? (
          <img src={shieldIconUrl} alt="" style={s.actionIcon} />
        ) : (
          <ShieldIcon />
        )}
      </button>
    );
  };

  const renderDetailBetRow = (round: BetHistoryRound, index: number) => {
    const isWin = round.cashOutAmount != null && round.cashOutAmount > 0;
    const displayMultiplier = getMyBetsMultiplier(round);
    const inProgress = isRoundInProgress(round, index);
    const { time, date } = formatDateParts(round.timestamp);
    const multiplierStyle = betHistoryMultiplierColors
      ? {
          ...s.multiplier,
          color: resolveBetHistoryMultiplierColor(
            displayMultiplier,
            betHistoryMultiplierColors
          ),
          fontWeight: 600,
        }
      : isWin
        ? s.multiplierWin
        : s.multiplier;
    const slotLabel = round.betSlot != null ? String(round.betSlot) : '—';
    const multiplierLabel = inProgress
      ? t('ui.popuppanel.bet_history.in_progress')
      : displayMultiplier > 0
        ? `${formatBetAmount(displayMultiplier)}x`
        : '—';

    return (
      <div
        key={`${round.roundId}-${round.betSlot ?? index}`}
        style={{ ...s.detailRow, ...detailGridStyle }}
      >
        <div style={s.dateCell}>
          <span style={s.dateTime}>{date}</span>
          <span style={s.dateDay}>{time}</span>
        </div>
        <span>{slotLabel}</span>
        <span style={s.numericCell}>{formatBetAmount(round.betAmount)}</span>
        <span style={{ ...multiplierStyle, ...s.numericCell }}>
          {multiplierLabel}
        </span>
        <span
          style={isWin ? { ...s.numericCell, ...s.cashOutWin } : s.numericCell}
        >
          {isWin ? formatBetAmount(round.cashOutAmount!) : '—'}
        </span>
        <div style={s.actions}>
          <span style={s.winCheck} aria-hidden>
            <WinCheckIcon />
          </span>
        </div>
      </div>
    );
  };

  const renderGroupedRows = () =>
    groups.map(group => {
      const expanded = expandedRoundIds.has(group.roundId);
      const canExpand = group.bets.length > 0;
      const summaryRound = group.bets[0]
        ? {
            ...group.bets[0],
            roundNo: group.bets[0].roundNo ?? group.roundNo,
          }
        : undefined;
      const isWin = (group.totalWin ?? 0) > 0;
      const rowStyle = isWin ? { ...s.row, ...s.rowWin } : s.row;

      return (
        <div key={group.roundId} style={s.groupWrap}>
          <div
            role={canExpand ? 'button' : undefined}
            tabIndex={canExpand ? 0 : undefined}
            onClick={
              canExpand ? () => toggleGroupExpanded(group.roundId) : undefined
            }
            onKeyDown={
              canExpand
                ? e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGroupExpanded(group.roundId);
                    }
                  }
                : undefined
            }
            aria-expanded={canExpand ? expanded : undefined}
            style={{
              ...rowStyle,
              ...summaryGridStyle,
              marginBottom: 0,
              cursor: canExpand ? 'pointer' : undefined,
            }}
          >
            {canExpand ? (
              <button
                type="button"
                style={s.expandButton}
                onClick={e => {
                  e.stopPropagation();
                  toggleGroupExpanded(group.roundId);
                }}
                aria-expanded={expanded}
                aria-label={
                  expanded
                    ? t('ui.popuppanel.bet_history.collapse')
                    : t('ui.popuppanel.bet_history.expand')
                }
              >
                <span style={s.expandIcon}>
                  <ExpandToggleIcon expanded={expanded} />
                </span>
              </button>
            ) : (
              <span />
            )}
            <span style={s.numericCell}>
              {formatBalanceAmount(group.startBalance)}
            </span>
            <span style={s.numericCell}>
              {formatBalanceAmount(group.totalBet)}
            </span>
            <span
              style={
                isWin ? { ...s.numericCell, ...s.cashOutWin } : s.numericCell
              }
            >
              {formatBalanceAmount(group.totalWin)}
            </span>
            <span style={s.endBalanceCell ?? s.numericCell}>
              {formatBalanceAmount(group.endBalance)}
            </span>
            <div
              style={s.actions}
              onClick={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
              onPointerDown={e => e.stopPropagation()}
            >
              {summaryRound ? renderShieldButton(summaryRound) : null}
            </div>
          </div>
          {expanded && canExpand ? (
            <div style={s.detailSection}>
              <div style={{ ...s.detailHead, ...detailGridStyle }}>
                <span style={s.headCell}>
                  {t('ui.popuppanel.bet_history.date')}
                </span>
                <span style={s.headCell}>
                  {t('ui.popuppanel.bet_history.bet_slot_column')}
                </span>
                <span style={s.headCellNumeric}>{betColumnLabel}</span>
                <span style={s.headCellNumeric}>
                  {t('ui.popuppanel.bet_history.multiplier')}
                </span>
                <span style={s.headCellNumeric}>{cashOutColumnLabel}</span>
                <span />
              </div>
              {group.bets.map((bet, betIndex) =>
                renderDetailBetRow(bet, betIndex)
              )}
            </div>
          ) : null}
        </div>
      );
    });

  const renderHistoryRows = () =>
    rounds.map((round, index) => {
      const isWin = round.cashOutAmount != null && round.cashOutAmount > 0;
      const displayMultiplier = resolveDisplayMultiplier(round, index);
      const inProgress = isRoundInProgress(round, index);
      const { time, date } = formatDateParts(round.timestamp);
      const rowStyle = isWin ? { ...s.row, ...s.rowWin } : s.row;
      const multiplierStyle = betHistoryMultiplierColors
        ? {
            ...s.multiplier,
            color: resolveBetHistoryMultiplierColor(
              displayMultiplier,
              betHistoryMultiplierColors
            ),
            fontWeight: 600,
          }
        : isWin
          ? s.multiplierWin
          : s.multiplier;

      return (
        <div
          key={`${round.roundId}-${round.betSlot ?? index}`}
          style={{ ...rowStyle, ...gridStyle }}
        >
          <div style={s.dateCell}>
            <span style={s.dateTime}>{date}</span>
            <span style={s.dateDay}>{time}</span>
          </div>
          {betHistoryShowBalances && (
            <span style={s.balanceCell}>
              {formatBalanceAmount(round.startBalance)}
            </span>
          )}
          <span style={s.numericCell}>{formatBetAmount(round.betAmount)}</span>
          <span style={{ ...multiplierStyle, ...s.numericCell }}>
            {inProgress
              ? t('ui.popuppanel.bet_history.in_progress')
              : `${formatBetAmount(displayMultiplier)}x`}
          </span>
          <span
            style={
              isWin ? { ...s.numericCell, ...s.cashOutWin } : s.numericCell
            }
          >
            {isWin ? formatBetAmount(round.cashOutAmount!) : ''}
          </span>
          {betHistoryShowBalances && (
            <span style={s.endBalanceCell ?? s.balanceCell}>
              {formatBalanceAmount(round.endBalance)}
            </span>
          )}
          <div style={s.actions}>
            {renderShieldButton(round)}
            {isWin ? (
              <span style={s.winCheck} aria-hidden>
                <WinCheckIcon />
              </span>
            ) : isSkyxNew ? null : (
              <button
                type="button"
                style={s.actionButton}
                aria-hidden
                tabIndex={-1}
              >
                {chatIconUrl ? (
                  <img src={chatIconUrl} alt="" style={s.actionIcon} />
                ) : (
                  <ChatIcon />
                )}
              </button>
            )}
          </div>
        </div>
      );
    });

  const panelStyle: React.CSSProperties = {
    ...s.panel,
    ...(useMobileCentered ? s.panelMobileCentered : {}),
    ...(betHistoryShowBalances && !useMobileCentered
      ? { maxWidth: isSkyxNew ? '820px' : '760px' }
      : {}),
    ...(useMobileCentered && betHistoryShowBalances
      ? { maxWidth: 'min(100%, 720px)' }
      : {}),
  };

  const panelBody = (
    <>
      {isSkyxNew && betHistoryModalCss ? (
        <style>{betHistoryModalCss}</style>
      ) : null}
      <header
        style={{
          ...s.header,
          ...(useMobileCentered ? s.headerMobile : {}),
          ...(isSkyxNew
            ? {
                borderBottom: isScrolled
                  ? '1px solid rgba(123,60,255,0.3)'
                  : '1px solid transparent',
                boxShadow: isScrolled
                  ? '0 8px 16px -6px rgba(0,0,0,0.4)'
                  : 'none',
                transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
              }
            : {}),
        }}
      >
        <div style={s.headerTitleGroup}>
          <h2 id="bet-history-title" style={s.title}>
            {t('ui.popuppanel.bet_history.title')}
          </h2>
          {!isSkyxNew && gameLogsUrl ? (
            <a
              href={gameLogsUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={s.logsLink}
              onClick={e => e.stopPropagation()}
            >
              {t('ui.popuppanel.bet_history.open_logs')}
            </a>
          ) : null}
        </div>
        <div style={s.headerActions}>
          {isSkyxNew ? <WalletBalance variant="modal" /> : null}
          {isSkyxNew && gameLogsUrl ? (
            <SkyxNewBetHistoryLogsButton
              ariaLabel={t('ui.popuppanel.bet_history.open_logs')}
              buttonStyle={{
                ...s.logsIconButton,
                ...(deviceType !== 'desktop'
                  ? SKYX_MODAL_CLOSE.buttonMobile
                  : {}),
              }}
              iconStyle={
                deviceType !== 'desktop'
                  ? { transform: 'scale(0.85)' }
                  : undefined
              }
              className="autoplay-3d-btn"
            />
          ) : null}
          {isSkyxNew ? (
            <SkyxNewModalCloseButton
              onClick={handleClose}
              ariaLabel={t('ui.common.close')}
              mobile={deviceType !== 'desktop'}
              variant="chip"
            />
          ) : (
            <button
              type="button"
              style={s.closeButton}
              onClick={handleClose}
              aria-label={t('ui.common.close')}
            >
              ×
            </button>
          )}
        </div>
      </header>

      {showHorizontalSlider && (
        <div style={s.horizontalScrollSliderWrap}>
          <input
            type="range"
            min={0}
            max={1}
            step={0.001}
            value={horizontalScrollRatio}
            onChange={handleHorizontalSliderChange}
            className="bet-history-horizontal-range"
            style={s.horizontalScrollRange}
            aria-label={t('ui.popuppanel.bet_history.scroll_table')}
          />
        </div>
      )}

      <div style={s.tableScrollWrap}>
        <div
          ref={horizontalScrollRef}
          className="bet-history-horizontal-scroll"
          style={s.tableHorizontalScroll}
        >
          <div
            style={{
              ...s.tableContent,
              ...(useMobileCentered ? s.tableContentMobile : {}),
              ...(tableMinWidth
                ? {
                    width: '100%',
                    minWidth: `max(100%, ${tableMinWidth}px)`,
                  }
                : { width: '100%' }),
            }}
          >
            <div
              style={{
                ...(isSkyxNew
                  ? { ...s.tableHead, ...summaryGridStyle }
                  : { ...s.tableHead, ...gridStyle }),
              }}
            >
              {isSkyxNew ? (
                <>
                  <span />
                  <span style={s.headCellNumeric}>
                    {startBalanceColumnLabel}
                  </span>
                  <span style={s.headCellNumeric}>{totalBetColumnLabel}</span>
                  <span style={s.headCellNumeric}>{totalWinColumnLabel}</span>
                  <span style={s.headCellEndBalance ?? s.headCellNumeric}>
                    {endBalanceColumnLabel}
                  </span>
                  <span />
                </>
              ) : (
                <>
                  <span style={s.headCell}>
                    {t('ui.popuppanel.bet_history.date')}
                  </span>
                  {betHistoryShowBalances && (
                    <span style={s.headCellNumeric}>
                      {startBalanceColumnLabel}
                    </span>
                  )}
                  <span style={s.headCellNumeric}>{betColumnLabel}</span>
                  <span style={s.headCellNumeric}>
                    {t('ui.popuppanel.bet_history.multiplier')}
                  </span>
                  <span style={s.headCellNumeric}>{cashOutColumnLabel}</span>
                  {betHistoryShowBalances && (
                    <span style={s.headCellEndBalance ?? s.headCellNumeric}>
                      {endBalanceColumnLabel}
                    </span>
                  )}
                  <span />
                </>
              )}
            </div>

            <div
              className="bet-list-scrollbar"
              style={{
                ...s.body,
              }}
              onScroll={
                isSkyxNew
                  ? e => setIsScrolled(e.currentTarget.scrollTop > 0)
                  : undefined
              }
            >
              {loading && isSkyxNew && s.loaderContainer ? (
                <div style={s.loaderContainer}>
                  <div className="spinner" style={s.circularLoader} />
                </div>
              ) : loading ? (
                <div style={s.statusMessage}>
                  {t('ui.popuppanel.bet_history.loading')}
                </div>
              ) : null}
              {!loading && error && <div style={s.statusMessage}>{error}</div>}
              {!loading && !error && groups.length === 0 ? (
                isSkyxNew && s.noBetsContainer ? (
                  <div style={s.noBetsContainer}>
                    <svg
                      width="36"
                      height="36"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={s.noBetsIcon}
                    >
                      <rect
                        x="4"
                        y="3"
                        width="16"
                        height="18"
                        rx="3"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M8 8h8M8 12h8M8 16h5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span>{t('ui.popuppanel.bet_history.empty')}</span>
                  </div>
                ) : (
                  <div style={s.statusMessage}>
                    {t('ui.popuppanel.bet_history.empty')}
                  </div>
                )
              ) : null}
              {!loading &&
                !error &&
                groups.length > 0 &&
                (isSkyxNew ? renderGroupedRows() : renderHistoryRows())}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const modal = (
    <div
      style={{
        ...s.overlay,
        ...(useMobileCentered ? s.overlayMobileCentered : {}),
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.2s ease',
      }}
      data-skyx-modal-backdrop
      onClick={handleClose}
      role="presentation"
    >
      {isSkyxNew ? (
        <SkyxNewModalPanelShell
          panelStyle={panelStyle}
          onClick={e => e.stopPropagation()}
          role="dialog"
          aria-labelledby="bet-history-title"
          aria-modal="true"
        >
          {panelBody}
        </SkyxNewModalPanelShell>
      ) : (
        <div
          style={panelStyle}
          onClick={e => e.stopPropagation()}
          role="dialog"
          aria-labelledby="bet-history-title"
          aria-modal="true"
        >
          {panelBody}
        </div>
      )}
    </div>
  );

  return createPortal(modal, document.body);
};
